import { Download, Close, Search } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Grid,
  Divider,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import Excel from "exceljs";
import FileServer from "file-saver";
import moment from "moment";

export interface ICSVColumnsFormat {
  columnName: string;
  fieldName: string;
  width?: number;
}

export interface ICSVDataFormat {
  [rowKey: string]: any;
}

export interface ICSVExportPluginonPagination {
  filePrefixName: string;
  columns: ICSVColumnsFormat[];
  allColumns?: ICSVColumnsFormat[];
  columnVisibility?: Record<string, { visible?: boolean; order?: number }>;
  enableColumnSelection?: boolean;
  /** Rows already loaded in the table. With `enableColumnSelection`, the column
   * picker is built from these, so the full export API is only hit once the
   * user confirms the download. */
  rows?: ICSVDataFormat[];
  onExportButtonClick: () => Promise<ICSVDataFormat[]>;
  onExportStart?: () => void;
  onExportComplete?: () => void;
  isMobile?: boolean;
}

const formatKeyToHeaderName = (key: string): string => {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const extractColumnsFromData = (
  data: ICSVDataFormat[],
  configuredCols: ICSVColumnsFormat[]
): ICSVColumnsFormat[] => {
  const keySet = new Set<string>();
  const sampleValues: Record<string, any> = {};

  if (Array.isArray(data)) {
    data.forEach((row) => {
      if (row && typeof row === "object") {
        Object.keys(row).forEach((key) => {
          if (!sampleValues.hasOwnProperty(key) || (sampleValues[key] === null || sampleValues[key] === undefined)) {
            sampleValues[key] = row[key];
          }
          keySet.add(key);
        });
      }
    });
  }

  const filteredKeys = Array.from(keySet).filter((key) => {
    const lowerKey = key.toLowerCase();
    // Exclude keys containing "uuid" or "id"
    if (lowerKey.includes("uuid") || lowerKey.includes("id")) {
      return false;
    }
    // Exclude keys whose non-null sample value is an object or array
    const val = sampleValues[key];
    if (typeof val === "object" && val !== null) {
      return false;
    }
    if (typeof val === "function") {
      return false;
    }
    return true;
  });

  const dynamicCols: ICSVColumnsFormat[] = filteredKeys.map((key) => {
    const matched = configuredCols.find((c) => c.fieldName === key);
    return {
      fieldName: key,
      columnName: matched ? matched.columnName : formatKeyToHeaderName(key),
      width: matched?.width || 20,
    };
  });

  // Keep configured columns that the sample rows didn't contain (e.g. every
  // loaded row had the key missing) so they can still be selected.
  configuredCols.forEach((col) => {
    if (!dynamicCols.some((c) => c.fieldName === col.fieldName)) {
      dynamicCols.push({ ...col, width: col.width || 20 });
    }
  });

  return dynamicCols;
};

export const CSVNew: React.FC<ICSVExportPluginonPagination> = (props) => {
  const {
    columns,
    allColumns,
    columnVisibility,
    enableColumnSelection = false,
    rows,
    filePrefixName,
    onExportButtonClick,
    onExportStart,
    onExportComplete,
    isMobile,
  } = props;

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [availableColumns, setAvailableColumns] = useState<ICSVColumnsFormat[]>([]);
  const [selectedColumnsMap, setSelectedColumnsMap] = useState<Record<string, boolean>>({});

  const computeInitialSelectedMap = (
    dynCols: ICSVColumnsFormat[]
  ): Record<string, boolean> => {
    const map: Record<string, boolean> = {};
    dynCols.forEach((col) => {
      if (columnVisibility && Object.keys(columnVisibility).length > 0) {
        if (columnVisibility[col.fieldName] !== undefined) {
          map[col.fieldName] = columnVisibility[col.fieldName]?.visible !== false;
        } else {
          map[col.fieldName] = columns.some((c) => c.fieldName === col.fieldName);
        }
      } else {
        map[col.fieldName] = columns.some((c) => c.fieldName === col.fieldName);
      }
    });
    return map;
  };

  const resetSelection = () => {
    setSelectedColumnsMap(computeInitialSelectedMap(availableColumns));
  };

  const filteredColumns = useMemo(() => {
    if (!searchTerm.trim()) return availableColumns;
    const term = searchTerm.toLowerCase();
    return availableColumns.filter(
      (col) =>
        col.columnName.toLowerCase().includes(term) ||
        col.fieldName.toLowerCase().includes(term)
    );
  }, [availableColumns, searchTerm]);

  const toggleSelectAll = (checked: boolean) => {
    const newMap: Record<string, boolean> = { ...selectedColumnsMap };
    filteredColumns.forEach((col) => {
      newMap[col.fieldName] = checked;
    });
    setSelectedColumnsMap(newMap);
  };

  const generateExcelSheet = async (
    content: ICSVDataFormat[],
    exportCols: ICSVColumnsFormat[]
  ) => {
    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet("Sheet1", {
      views: [{ state: "frozen", ySplit: 1 }],
    });
    const generatedColumns = [];

    for (let column of exportCols) {
      generatedColumns.push({
        header: column.columnName,
        key: column.columnName,
        width: column.width || 20,
      });
    }
    sheet.columns = generatedColumns;
    const rowsList: any = [];

    const reversedContent = [...content].reverse();
    for (let row of reversedContent) {
      const finalRow = [];
      for (let col of exportCols) {
        let data = row[col.fieldName];
        if (
          typeof data === "string" &&
          moment(data, "YYYY-MM-DDTHH:mm:ss.SSSZ", true).isValid()
        ) {
          data = moment(data).format("DD-MM-YYYY HH:mm");
        } else if (typeof data === "object" && data !== null) {
          data = JSON.stringify(data);
        }

        finalRow.push(data ?? "");
      }
      rowsList.push(finalRow);
    }
    const date = moment(new Date()).format("DD-MM-YYYY");
    sheet.addRows(rowsList);
    const buffer = await workbook.xlsx.writeBuffer();
    FileServer.saveAs(new Blob([buffer]), `${filePrefixName}-${date}.xlsx`);
  };

  const handleExportClick = async () => {
    if (enableColumnSelection) {
      // Build the column picker from what the table already has; the full
      // data is fetched only when the user confirms the download.
      const dynCols =
        allColumns && allColumns.length > 0
          ? allColumns
          : extractColumnsFromData(rows || [], columns);

      setAvailableColumns(dynCols);
      setSelectedColumnsMap(computeInitialSelectedMap(dynCols));
      setSearchTerm("");
      setDialogOpen(true);
      return;
    }

    try {
      if (onExportStart) {
        onExportStart();
      }
      setLoading(true);
      const data = await onExportButtonClick();
      await generateExcelSheet(data, columns);
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setLoading(false);
      if (onExportComplete) {
        onExportComplete();
      }
    }
  };

  const handleConfirmDownload = async () => {
    const colsToExport = availableColumns.filter(
      (col) => selectedColumnsMap[col.fieldName]
    );
    if (colsToExport.length === 0) {
      alert("Please select at least one column to export.");
      return;
    }

    try {
      setExporting(true);
      if (onExportStart) {
        onExportStart();
      }
      const data = await onExportButtonClick();
      await generateExcelSheet(data, colsToExport);
      setDialogOpen(false);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setExporting(false);
      if (onExportComplete) {
        onExportComplete();
      }
    }
  };

  return (
    <>
      {isMobile ? (
        <Tooltip title="Download Excel file">
          <span>
            <IconButton
              onClick={handleExportClick}
              disabled={loading}
              color="primary"
            >
              {loading ? <CircularProgress size={24} /> : <Download />}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Download />}
          onClick={handleExportClick}
          sx={{ ml: 2 }}
          disabled={loading}
        >
          {loading ? "Fetching Data..." : "Download"}
        </Button>
      )}

      {enableColumnSelection && (
        <Dialog
          open={dialogOpen}
          onClose={() => !exporting && setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justify: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Select Columns to Download
            </Typography>
            <IconButton
              onClick={() => setDialogOpen(false)}
              disabled={exporting}
              size="small"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justify: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1.5,
                mb: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Search columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                sx={{ flexGrow: 1 }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => toggleSelectAll(true)}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => toggleSelectAll(false)}
                >
                  Deselect All
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={resetSelection}
                >
                  Reset Default
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1} sx={{ maxHeight: 380, overflowY: "auto" }}>
              {filteredColumns.map((col) => {
                const isChecked = !!selectedColumnsMap[col.fieldName];
                return (
                  <Grid item xs={12} sm={6} md={4} key={col.fieldName}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) =>
                            setSelectedColumnsMap((prev) => ({
                              ...prev,
                              [col.fieldName]: e.target.checked,
                            }))
                          }
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" noWrap title={col.columnName}>
                          {col.columnName}
                        </Typography>
                      }
                      sx={{ width: "100%", ml: 0 }}
                    />
                  </Grid>
                );
              })}
              {filteredColumns.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" align="center" py={2}>
                    No columns match your search.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              disabled={exporting}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmDownload}
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <Download />}
            >
              {exporting ? "Downloading..." : "Download Excel"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};


